import { buildVariants } from './variants';
import type { DesignVariant } from './types';

/**
 * Heuristic: rough perceived lightness of a hex color (0-1).
 * Returns 0.5 if parsing fails so the value is neutral and never biases the sort.
 */
function hexLightness(hex: string): number {
  const m = hex.replace(/^#/, '').match(/^([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return 0.5;
  let r: number, g: number, b: number;
  const raw = m[1];
  if (raw.length === 3) {
    r = parseInt(raw[0]! + raw[0]!, 16) / 255;
    g = parseInt(raw[1]! + raw[1]!, 16) / 255;
    b = parseInt(raw[2]! + raw[2]!, 16) / 255;
  } else {
    r = parseInt(raw.slice(0, 2)!, 16) / 255;
    g = parseInt(raw.slice(2, 4)!, 16) / 255;
    b = parseInt(raw.slice(4, 6)!, 16) / 255;
  }
  return (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
}

/**
 * Pick which variants to surface based on the current form theme.
 *
 * Each vibe slot (Bold, Sleek, Soft) has a small pool of theme options;
 * `rotation` selects which option from each pool is shown, so consecutive
 * shuffles cycle through different looks while keeping the slot identities
 * stable (so user tier toggles and media pools survive a shuffle).
 *
 * Returned variants are still ordered by visual contrast against the form's
 * current background so the most transformative option appears first.
 */
export function pickVariantsForForm(
  currentBackgroundHex: string | null | undefined,
  rotation = 0,
): DesignVariant[] {
  const variants = buildVariants(rotation);
  const currentLightness = hexLightness(currentBackgroundHex ?? '#ffffff');
  const isCurrentlyDark = currentLightness < 0.5;

  const scored = variants.map(v => {
    const variantLightness = hexLightness(v.theme.backgroundColorSetting);
    const lightnessDelta = Math.abs(variantLightness - currentLightness);
    const orientationBoost = (variantLightness < 0.5) === isCurrentlyDark ? 0 : 0.25;
    return { variant: v, score: lightnessDelta + orientationBoost };
  });

  scored.sort((a, b) => b.score - a.score);

  return scored.map(s => s.variant);
}
